export interface TranscriptData {
  conversationId: string;
  fullTranscript: string;
  timestampedMessages: Array<{
    timestamp: number;
    speaker: 'interviewer' | 'candidate';
    message: string;
    confidence: number;
    emotions?: any[];
  }>;
  audioMetrics: {
    totalSilenceDuration: number;
    averageResponseTime: number;
    interruptionCount: number;
    clarityScore: number;
  };
}

export interface CodeData {
  language: string;
  initialCode: string;
  finalCode: string;
  codeHistory: Array<{
    timestamp: number;
    code: string;
    changeType: 'addition' | 'deletion' | 'modification';
    linesChanged: number[];
  }>;
  syntaxErrors: Array<{
    timestamp: number;
    error: string;
    line: number;
    resolved: boolean;
    resolutionTime?: number;
  }>;
}

export interface SessionContext {
  sessionTime: string;
  minutesUsed: number;
  problemTitle?: string;
  difficulty?: string;
  problemDescription: string;
  problemConstraints: string[];
  problemExamples: Array<{
    input: string;
    output: string;
    explanation?: string;
  }>;
  companyContext?: string;
  userPreferences: {
    isStructured: boolean;
    isLeetCode?: boolean;
    difficulty?: string;
    area?: string;
    companyStyle?: string;
    freeFormText?: string;
    // LeetCode-specific fields
    title?: string;
    description?: string;
    examples?: string;
    constraints?: string;
  };
}

export interface AnalysisRequest {
  transcriptData: TranscriptData;
  codeData: CodeData;
  sessionContext: SessionContext;
}

export interface CategoryScore {
  category:
    | 'Problem Understanding'
    | 'Solution Approach'
    | 'Code Quality'
    | 'Communication'
    | 'Time Management'
    | 'Debugging Skills';
  score: number; // 0–100
  insufficient: boolean;
  confidence: number; // 0–100
  evidence: string[]; // 0–3 short quotes/snippets
  feedback: string;
  improvement_suggestions: string[];
}

export interface PerformanceAnalysis {
  overallScore: number;
  categoryScores: CategoryScore[];
}

export interface OptimalSolution {
  code: string;
  language: string;
  timeComplexity: string;
  spaceComplexity: string;
  explanation: string;
  alternativeApproaches?: Array<{
    approach: string;
    code: string;
    complexity: string;
    tradeoffs: string;
  }>;
}

export interface DetailedFeedback {
  strengths: string[];
  improvements: Array<{
    category: string;
    suggestion: string;
    impact: string;
    actionableSteps: string[];
  }>;
  studyPlan: Array<{
    topic: string;
    priority: 'high' | 'medium' | 'low';
    resources: string[];
    timeEstimate: string;
  }>;
}

export interface TranscriptSummary {
  keyMoments: Array<{
    timestamp: number;
    description: string;
    significance: string;
  }>;
  communicationPatterns: {
    clarity: number;
    confidence: number;
    technical_accuracy: number;
    engagement: number;
  };
}

export interface AnalysisResponse {
  result_id: string;
  analyzed_at: string;
  problem_title: string;
  difficulty: string;
  overall_score: number | null;
}

export interface FullAnalysisResult {
  result_id: string;
  created_at: string;
  updated_at: string;
  status: string; // Analysis status (completed, pending, failed, etc.)

  // NEW: Analysis Quality Indicators
  insufficientData: boolean;
  dataCoverage: {
    transcriptChars: number;
    finalCodeChars: number;
    codeHistoryCount: number;
  };

  sessionMetadata: {
    sessionTime: string;
    minutesUsed: number;
    problemTitle: string;
    difficulty: string;
    language: string;
    companyContext: string;
    problemDescription: string;
    problemConstraints: string[];
    problemExamples: Array<{
      input: string;
      output: string;
      explanation?: string;
    }>;
    userPreferences?: {
      difficulty?: string;
      topics?: string[];
      companies?: string[];
    };
  };
  codeProgression: {
    language: string;
    initialCode: string;
    finalCode: string;
    codeHistory: Array<{
      timestamp: number;
      code: string;
      changeType: 'addition' | 'deletion' | 'modification';
      linesChanged: number[];
    }>;
    syntaxErrors: Array<{
      timestamp: number;
      error: string;
      line: number;
      resolved: boolean;
      resolutionTime?: number;
    }>;
  };
  transcriptData: {
    conversationId: string;
    fullTranscript: string;
    timestampedMessages: Array<{
      timestamp: number;
      speaker: 'interviewer' | 'candidate';
      message: string;
      confidence: number;
      emotions?: any[];
    }>;
    audioMetrics: {
      totalSilenceDuration: number;
      averageResponseTime: number;
      interruptionCount: number;
      clarityScore: number;
      speakingPace?: string;
      confidenceLevel?: number;
    };
  };
  performanceAnalysis: {
    overallScore: number;
    categoryScores: CategoryScore[];
  };
  optimalSolution: {
    code: string;
    language: string;
    timeComplexity: string;
    spaceComplexity: string;
    explanation: string;
    alternativeApproaches: Array<{
      approach: string;
      code: string;
      complexity: string;
      tradeoffs: string;
    }>;
  };
  detailedFeedback: {
    strengths: string[];
    improvements: Array<{
      category: string;
      suggestion: string;
      impact: string;
      actionableSteps: string[];
    }>;
    studyPlan: Array<{
      topic: string;
      priority: 'high' | 'medium' | 'low';
      resources: string[];
      timeEstimate: string;
    }>;
  };
  transcriptSummary: {
    keyMoments: Array<{
      timestamp: number;
      description: string;
      significance: string;
    }>;
    communicationPatterns: {
      clarity: number;
      confidence: number;
      technical_accuracy: number;
      engagement: number;
      pace?: string;
      filler_words?: string;
      question_asking?: string;
      explanation_quality?: string;
    };
  };
}

export interface AnalysisResultSummary {
  result_id: string;
  created_at: string;
  problem_title: string;
  difficulty: string;
  language: string;
  overall_score: number | null;
  session_time: string;
  status: string;
}

export interface AnalysisResultsList {
  results: AnalysisResultSummary[];
  total_count: number;
}

class AnalysisService {
  private baseURL: string;

  constructor() {
    this.baseURL =
      process.env.NODE_ENV === 'development'
        ? 'http://localhost:8000'
        : 'https://stronghireai.com';
  }

  async startInterviewAnalysis(
    chatGroupId: string,
    codeData: any,
    sessionContext: any
  ): Promise<{ result_id: string }> {
    try {
      const response = await fetch(
        `${this.baseURL}/openai_api/analyze_interview_v2/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include JWT token from cookies
          body: JSON.stringify({
            chatGroupId,
            codeData,
            sessionContext,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Failed to start analysis: ${response.status} ${
            response.statusText
          } - ${errorData.error || errorData.message || 'Unknown error'}`
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error starting interview analysis:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(
          'Cannot connect to backend server. Please ensure your Django server is running on ' +
            this.baseURL
        );
      }
      throw error;
    }
  }

  async getAnalysisResult(resultId: string): Promise<FullAnalysisResult> {
    try {
      const response = await fetch(
        `${this.baseURL}/openai_api/results/${resultId}/`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Failed to get analysis result: ${response.status} ${
            response.statusText
          } - ${errorData.error || errorData.message || 'Unknown error'}`
        );
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('Error getting analysis result:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(
          'Cannot connect to backend server. Please ensure your Django server is running on ' +
            this.baseURL
        );
      }
      throw error;
    }
  }

  async listAnalysisResults(): Promise<AnalysisResultsList> {
    try {
      const response = await fetch(`${this.baseURL}/openai_api/results/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Failed to get analysis results: ${response.status} ${
            response.statusText
          } - ${errorData.error || errorData.message || 'Unknown error'}`
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error listing analysis results:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(
          'Cannot connect to backend server. Please ensure your Django server is running on ' +
            this.baseURL
        );
      }
      throw error;
    }
  }

  // Extract transcript data from Hume AI voice-react messages
  extractTranscriptData(
    messages: any[],
    conversationId: string,
    sessionStartTime: number,
    language: string = 'coding'
  ): TranscriptData {
    const timestampedMessages: TranscriptData['timestampedMessages'] = [];
    let interruptionCount = 0;
    let totalResponseTime = 0;
    let responseCount = 0;
    let lastUserMessageTime = 0;

    // If no messages, provide a fallback transcript
    if (!messages || messages.length === 0) {
      console.warn('No messages found, using fallback transcript');
      return {
        conversationId,
        fullTranscript: '',
        timestampedMessages: [],
        audioMetrics: {
          totalSilenceDuration: 0,
          averageResponseTime: 2000,
          interruptionCount: 0,
          clarityScore: 0,
        },
      };
    }

    // Process messages to extract transcript and metrics
    messages.forEach((message, index) => {
      const timestamp = sessionStartTime + index * 1000;

      // Handle different message structure formats
      let messageContent = '';
      let messageType = message.type;

      // Hume AI specific message handling
      if (message.type === 'user_message') {
        // User/candidate message from Hume AI
        if (message.message && message.message.content) {
          messageContent = message.message.content;
        } else if (message.content) {
          messageContent = message.content;
        }
        messageType = 'user_message';
      } else if (message.type === 'assistant_message') {
        // Assistant/interviewer message from Hume AI
        if (message.message && message.message.content) {
          messageContent = message.message.content;
        } else if (message.content) {
          messageContent = message.content;
        }
        messageType = 'assistant_message';
      } else {
        // Fallback for other message types
        // Try different ways to extract message content
        if (message.message?.content) {
          messageContent = message.message.content;
        } else if (message.content) {
          messageContent = message.content;
        } else if (message.text) {
          messageContent = message.text;
        } else if (typeof message === 'string') {
          messageContent = message;
        }

        // If we can't determine the type, try to infer from other properties
        if (!messageType) {
          if (message.role === 'user' || message.sender === 'user') {
            messageType = 'user_message';
          } else if (
            message.role === 'assistant' ||
            message.sender === 'assistant'
          ) {
            messageType = 'assistant_message';
          }
        }
      }

      // Only process messages with actual content
      if (messageContent && messageContent.trim()) {
        if (messageType === 'user_message' || messageType === 'user') {
          timestampedMessages.push({
            timestamp,
            speaker: 'candidate',
            message: messageContent,
            confidence: message.confidence || 1.0,
            emotions: message.emotions || [],
          });
          lastUserMessageTime = timestamp;
        } else if (
          messageType === 'assistant_message' ||
          messageType === 'assistant'
        ) {
          timestampedMessages.push({
            timestamp,
            speaker: 'interviewer',
            message: messageContent,
            confidence: 1.0,
          });

          // Calculate response time if we have a previous user message
          if (lastUserMessageTime > 0) {
            const responseTime = timestamp - lastUserMessageTime;
            totalResponseTime += responseTime;
            responseCount++;
          }
        }
      } else if (messageType === 'user_interruption') {
        interruptionCount++;
      } else {
      }
    });

    // Create full transcript
    const fullTranscript =
      timestampedMessages.length > 0
        ? timestampedMessages
            .map(
              (msg) =>
                `${msg.speaker === 'candidate' ? 'You' : 'AI Interviewer'}: ${
                  msg.message
                }`
            )
            .join('\n\n')
        : 'Interview session completed with voice interaction.'; // Fallback if no processable messages

    // If we still have no processable messages, log a warning and provide enhanced fallback
    if (timestampedMessages.length === 0 && messages.length > 0) {
      console.warn(
        'No messages could be processed from Hume AI. Message types found:',
        messages.map((m) => m.type || 'unknown_type')
      );
      console.warn(
        'Sample message structure:',
        JSON.stringify(messages[0], null, 2)
      );

      return {
        conversationId,
        fullTranscript: '',
        timestampedMessages: [],
        audioMetrics: {
          totalSilenceDuration: 0,
          averageResponseTime: 3000,
          interruptionCount: 0,
          clarityScore: 0,
        },
      };
    }

    return {
      conversationId,
      fullTranscript,
      timestampedMessages,
      audioMetrics: {
        totalSilenceDuration: 0, // Would need WebRTC audio analysis
        averageResponseTime:
          responseCount > 0 ? totalResponseTime / responseCount : 2000,
        interruptionCount,
        clarityScore: 1, // Placeholder - would need audio analysis
      },
    };
  }

  // Extract code evolution data from tracking
  extractCodeData(codeHistory: any[], language: string): CodeData {
    const initialCode = codeHistory.length > 0 ? codeHistory[0].code : '';
    const finalCode =
      codeHistory.length > 0 ? codeHistory[codeHistory.length - 1].code : '';

    // Process code history to detect change types
    const processedHistory = codeHistory.map((entry, index) => {
      const prevCode = index > 0 ? codeHistory[index - 1].code : '';
      const currentCode = entry.code;

      // Simple change detection
      let changeType: 'addition' | 'deletion' | 'modification' = 'modification';
      if (currentCode.length > prevCode.length) {
        changeType = 'addition';
      } else if (currentCode.length < prevCode.length) {
        changeType = 'deletion';
      }

      return {
        timestamp: entry.timestamp,
        code: currentCode,
        changeType,
        linesChanged: [1], // Placeholder - would need proper diff analysis
      };
    });

    return {
      language,
      initialCode,
      finalCode,
      codeHistory: processedHistory,
      syntaxErrors: [], // Would need to be tracked during coding
    };
  }
}

const analysisService = new AnalysisService();
export default analysisService;
